import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [pushState, setPushState] = useState("default"); // default | granted | denied | unsupported
  const [updateReady, setUpdateReady] = useState(false);
  const regRef = useRef(null);
  const waitingRef = useRef(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      regRef.current = reg;
      if (reg.waiting && navigator.serviceWorker.controller) { waitingRef.current = reg.waiting; setUpdateReady(true); }
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            waitingRef.current = nw; setUpdateReady(true);
          }
        });
      });
      reg.pushManager?.getSubscription?.().then((sub) => {
        if (sub) setPushState("granted");
      });
    }).catch(() => {});

    if (!("Notification" in window) || !("PushManager" in window)) setPushState("unsupported");
    else if (Notification.permission === "granted") setPushState("granted");
    else if (Notification.permission === "denied") setPushState("denied");

    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") { setDeferredPrompt(null); return true; }
    return false;
  }, [deferredPrompt]);

  const applyUpdate = useCallback(() => {
    const w = waitingRef.current;
    if (w) { w.postMessage("SKIP_WAITING"); }
    window.location.reload();
  }, []);

  const enablePush = useCallback(async () => {
    if (!("Notification" in window) || !("PushManager" in window)) { setPushState("unsupported"); return { ok: false, reason: "unsupported" }; }
    if (isIOS() && !isStandalone()) return { ok: false, reason: "ios-not-installed" };
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { setPushState(perm); return { ok: false, reason: "denied" }; }
    const reg = regRef.current || (await navigator.serviceWorker.ready);
    const { data } = await api.get("/push/vapid-public-key");
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    const json = sub.toJSON();
    await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
    setPushState("granted");
    return { ok: true };
  }, []);

  const testPush = useCallback(() => api.post("/push/test"), []);

  return { deferredPrompt, installed, pushState, updateReady, promptInstall, applyUpdate, enablePush, testPush, isIOS: isIOS(), isStandalone: isStandalone() };
}
